import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const api = axios.create({ baseURL: API });

// Add auth header interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("qf_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Interceptor: normaliza 402 (plan_limit) para que el `detail.message` quede accesible como string
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 402 && typeof err.response.data?.detail === "object") {
      const d = err.response.data.detail;
      err.response.data.detail = d.message || "Límite del plan alcanzado.";
      err.response.data.planLimit = d;
    }
    return Promise.reject(err);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPlan = useCallback(async () => {
    try {
      const { data } = await api.get("/plan");
      setPlan(data);
    } catch { setPlan(null); }
  }, []);

  // Refresca el user contra /auth/me al cargar si hay token
  useEffect(() => {
    const token = localStorage.getItem("qf_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then(async ({ data }) => {
        setUser(data);
        localStorage.setItem("qf_user", JSON.stringify(data));
        await loadPlan();
      })
      .catch(() => {
        localStorage.removeItem("qf_token");
        localStorage.removeItem("qf_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [loadPlan]);

  const login = (token, userData) => {
    if (token) localStorage.setItem("qf_token", token);
    localStorage.setItem("qf_user", JSON.stringify(userData));
    setUser(userData);
    loadPlan();
  };

  const logout = () => {
    localStorage.removeItem("qf_token");
    localStorage.removeItem("qf_user");
    setUser(null);
    setPlan(null);
  };

  return (
    <AuthContext.Provider value={{ user, plan, refreshPlan: loadPlan, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

// Utilidad para chequear features en cualquier componente
export function hasFeature(plan, feature) {
  if (!plan?.features) return false;
  return !!plan.features[feature];
}
